import { ArcVoiceSpeaker } from '@common/interfaces/arcVoice'
import { HUDDLE_INSIGHT_LINE_MAX_CHARS, HUDDLE_INSIGHT_MAX_LINES } from '@common/interfaces/huddles'
import { IArcVoiceCaption } from './realtimeVoiceClient'
import { isAddressedInRoom } from './roomTurns'

/** At most one check this often, and only once this much new talk has come in. */
export const INSIGHT_MIN_INTERVAL_MS = 20 * 1000
export const INSIGHT_MIN_NEW_CHARS = 120
const ARC_LINES_KEPT = 5

export interface IRoomInsightDeps {
  judge: (lines: string[], arcSaid: string[]) => Promise<string | null>
  speak: (instruction: string) => void
  isBusy: () => boolean
  now?: () => number
}

/** How Arc chimes in unasked. The check already decided it's worth saying, so the voice says it. */
export const insightPrompt = (instruction: string): string =>
  `Nobody asked you, so chime in briefly and naturally, in one or two spoken sentences: ${instruction}`

/**
 * Arc in a huddle speaks when spoken to; this lets it speak up when it has something
 * worth saying. Every so often, given enough new talk, the room's latest lines go to a
 * cheap check, and a yes becomes one short turn for the voice.
 */
export class RoomInsightGate {
  private heard: string[] = []

  private arcSaid: string[] = []

  private newChars = 0

  private lastJudgedAt = Number.NEGATIVE_INFINITY

  private isJudging = false

  private isClosed = false

  constructor(private readonly deps: IRoomInsightDeps) {}

  hear({ speaker, text, isFinal }: IArcVoiceCaption): void {
    const line = text.trim().slice(0, HUDDLE_INSIGHT_LINE_MAX_CHARS)
    if (this.isClosed || !isFinal || !line) return
    if (speaker === ArcVoiceSpeaker.Arc) {
      this.arcSaid = [...this.arcSaid, line].slice(-ARC_LINES_KEPT)
      return
    }
    this.heard = [...this.heard, line].slice(-HUDDLE_INSIGHT_MAX_LINES)
    // Spoken to, Arc answers anyway: the next check waits for talk after that.
    if (isAddressedInRoom(line)) {
      this.newChars = 0
      this.lastJudgedAt = this.now()
      return
    }
    this.newChars += line.length
    void this.maybeJudge()
  }

  close(): void {
    this.isClosed = true
  }

  private async maybeJudge(): Promise<void> {
    const now = this.now()
    const isDue =
      this.newChars >= INSIGHT_MIN_NEW_CHARS && now - this.lastJudgedAt >= INSIGHT_MIN_INTERVAL_MS
    if (this.isJudging || !isDue || this.deps.isBusy()) return
    this.isJudging = true
    this.lastJudgedAt = now
    this.newChars = 0
    try {
      const instruction = await this.deps.judge(this.heard, this.arcSaid)
      if (instruction && !this.isClosed && !this.deps.isBusy()) {
        this.deps.speak(insightPrompt(instruction))
      }
    } catch {
      // A failed check is a moment of silence; the next lines ask again.
    } finally {
      this.isJudging = false
    }
  }

  private now(): number {
    return this.deps.now?.() ?? Date.now()
  }
}
